// src/components/gatherings/GatheringEditForm.tsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Typography, 
  Divider, 
  Box, 
  Paper,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  MenuItem,
  InputAdornment,
  Grid,
  Select,
  Backdrop,
  CircularProgress,
  Avatar
} from '@mui/material';
import { parseISO, format } from 'date-fns';
import { Gathering, GatheringStatus, Interest, Area } from '../../../../types';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PeopleIcon from '@mui/icons-material/People';
import CategoryIcon from '@mui/icons-material/Category';
import NotesIcon from '@mui/icons-material/Notes';
import LinkIcon from '@mui/icons-material/Link';
import PublicIcon from '@mui/icons-material/Public';
import StarIcon from '@mui/icons-material/Star';
import BuildIcon from '@mui/icons-material/Build';
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import { generateUploadUrl } from '../../../../services/api/endpoints/documents';
import RichTextEditor from '../../../../shared/components/RichTextEditor';
import { descriptionToEditorHtml } from '../../../../utils/richText';

interface GatheringEditFormProps {
  gathering: Gathering;
  onSave?: (updatedGathering: Partial<Gathering>) => Promise<void>;
  onCancel?: () => void;
  isSaving?: boolean;
  token?: string;
}

// Define which fields can be edited
interface EditableGatheringFields {
  title: string;
  description: string;
  category: string;
  event_size: number;
  additional_notes: string;
  is_public: boolean;
  is_members_only: boolean;
  is_featured: boolean;
  is_fabrik_hosted: boolean;
  is_builder_gathering: boolean;
  luma_link: string;
  community: string;
}

const GatheringEditForm: React.FC<GatheringEditFormProps> = ({ 
  gathering,
  onSave,
  onCancel,
  isSaving = false,
  token
}) => {
  // The HubSpot sync only runs for gatherings that have been published, so the
  // Builder Gathering helper text has to explain when the flag actually lands there
  const isPublished = gathering.status === GatheringStatus.PUBLISHED;

  // Helper function to validate URL
  const isValidUrl = (url: string): boolean => {
    if (!url) return true; // Empty URL is considered valid (but will be excluded from payload)
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  // State to track form values
  const [formValues, setFormValues] = useState<EditableGatheringFields>({
    title: gathering.title,
    description: gathering.description,
    category: gathering.category,
    event_size: gathering.event_size,
    additional_notes: gathering.additional_notes || '',
    is_public: gathering.is_public,
    is_members_only: gathering.is_members_only,
    is_featured: gathering.is_featured,
    is_fabrik_hosted: gathering.is_fabrik_hosted,
    is_builder_gathering: !!gathering.is_builder_gathering,
    luma_link: gathering.luma_link || '',
    community: gathering.community || ''
  });

  // State to track validation errors
  const [validationErrors, setValidationErrors] = useState<{
    title?: string;
    community?: string;
  }>({});

  // State to track if form has been modified
  const [isModified, setIsModified] = useState<boolean>(false);
  
  // State to track which field was last edited
  const [lastEditedField, setLastEditedField] = useState<keyof EditableGatheringFields | null>(null);
  
  // Image upload (minimal) state
  const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Local preview for optimistic display (uploaded file or default placeholder)
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  // Track a pending image change that should only persist on Save
  // null = no pending change; '' = use backend default on save; string = specific object key to save
  const [pendingImageObjectKey, setPendingImageObjectKey] = useState<string | null>(null);

  const openImagePicker = () => fileInputRef.current?.click();

  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!onSave) return;
    if (!token) { alert('Missing auth token for upload.'); return; }

    const maxSizeMB = 10;
    if (file.size > maxSizeMB * 1024 * 1024) {
      alert(`File is too large. Max size is ${maxSizeMB} MB.`);
      return;
    }

    const parts = file.name.split('.');
    const ext = parts.length > 1 ? parts.pop()!.toLowerCase() : '';
    const base = parts.join('.') || 'image';
    if (!ext) {
      alert('Please select a file with an extension (e.g., .jpg, .png).');
      return;
    }

    try {
      setIsUploadingImage(true);
      // Optimistic preview of the selected image
      const objectUrl = URL.createObjectURL(file);
      setLocalPreviewUrl(objectUrl);
      setPendingImageObjectKey(null); // clear any pending default

      const presign = await generateUploadUrl(token, {
        file_name: base,
        file_extension: ext,
        element: 'Gatherings'
      });

      const putResp = await fetch(presign.url, {
        method: 'PUT',
        headers: { 'Content-Type': presign.mime_type },
        body: file,
      });
      if (!putResp.ok) {
        throw new Error(`S3 upload failed with status ${putResp.status}`);
      }

      // Do NOT auto-save/close the form. Stash the new object key and require the user to click Save.
      setPendingImageObjectKey(presign.object_key);
      setIsModified(true);
    } catch (err) {
      console.error('Image upload failed:', err);
      alert('Image failed to upload. Please try again.');
      setLocalPreviewUrl(null);
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleResetToDefaultImage = async () => {
    // Non-destructive UX: mark intent to use default, preview placeholder, defer persistence to Save
    setPendingImageObjectKey('');
    setLocalPreviewUrl(null); // ensure we show the placeholder preview instead of any prior upload preview
    setIsModified(true);
  };

  // Update form values if gathering changes
  useEffect(() => {
    setFormValues({
      title: gathering.title,
      description: gathering.description,
      category: gathering.category,
      event_size: gathering.event_size,
      additional_notes: gathering.additional_notes || '',
      is_public: gathering.is_public,
      is_members_only: gathering.is_members_only,
      is_featured: gathering.is_featured,
      is_fabrik_hosted: gathering.is_fabrik_hosted,
      is_builder_gathering: !!gathering.is_builder_gathering,
      luma_link: gathering.luma_link || '',
      community: gathering.community || ''
    });
    setIsModified(false);
    setLocalPreviewUrl(null);
    setPendingImageObjectKey(null);
  }, [gathering]);
  
  // Generic change handler for form fields
  const handleChange = (field: keyof EditableGatheringFields, value: any) => {
    setLastEditedField(field); // Track which field was last edited
    
    setFormValues(prev => {
      // Handle mutual exclusivity for is_public and is_members_only
      if (field === 'is_public' && value === true) {
        return {
          ...prev,
          is_public: true,
          is_members_only: false
        };
      }
      if (field === 'is_members_only' && value === true) {
        return {
          ...prev,
          is_public: false,
          is_members_only: true
        };
      }
      // For all other fields, handle normally
      return {
        ...prev,
        [field]: value
      };
    });
    setIsModified(true);
  };
  
  // Handle form submission
  const handleSave = async () => {
    if (onSave && isModified) {
      try {
        // Reset validation errors
        setValidationErrors({});
        
        // Validate required fields
        const errors: {
          title?: string;
          community?: string;
        } = {};
        
        if (!formValues.title || formValues.title.trim() === '') {
          errors.title = 'Title is required';
        }
        
        if (!formValues.community || formValues.community.trim() === '') {
          errors.community = 'Community is required';
        } else if (formValues.community.trim().length < 3) {
          errors.community = 'Community name must be at least 3 characters';
        }
        
        // If there are validation errors, display them and stop submission
        if (Object.keys(errors).length > 0) {
          setValidationErrors(errors);
          return;
        }
        
        // Validate Luma link if provided
        if (formValues.luma_link && !isValidUrl(formValues.luma_link)) {
          alert('Please enter a valid URL for the Luma link or leave it empty.');
          return;
        }
        
        // Create a copy of form values without the empty luma_link
        const { luma_link, ...restValues } = formValues;
        
        // Only include luma_link if it's not empty
        const formattedValues = {
          ...restValues,
          ...(luma_link ? { luma_link } : {})
        };
        
        // Only include fields that have changed compared to the original gathering
        const changedFields: Record<string, unknown> = {};
        if (gathering) {
          (Object.keys(formattedValues) as Array<keyof typeof formattedValues>).forEach(key => {
            if (gathering[key] !== formattedValues[key]) {
              changedFields[key] = formattedValues[key];
            }
          });
        }
        // Include pending image change if present ('' means use default on backend)
        if (pendingImageObjectKey !== null) {
          changedFields.image_object_key = pendingImageObjectKey;
          // Backend needs space_id when image_object_key is '' to resolve default image
          if (pendingImageObjectKey === '' && gathering.space?.id) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore: update endpoint accepts space_id
            changedFields.space_id = gathering.space.id;
          }
        }
        await onSave(changedFields as Partial<Gathering>);
      } catch (error) {
        // Show error message to user
        alert('Failed to save changes. Please try again.');
      }
    }
  };
  
  // Handle cancel with confirmation if form is modified
  const handleCancel = () => {
    if (isModified) {
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        if (onCancel) onCancel();
      }
    } else {
      if (onCancel) onCancel();
    }
  };
  
  return (
    <Box sx={{ position: 'relative' }}>
      {/* Loading overlay */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          position: 'absolute',
          borderRadius: 1
        }}
        open={isSaving}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      
      <Box sx={{ width: '100%' }}>
        {/* Header with title */}
        <Box sx={{ mb: 1 }}>
          <TextField
            label="Title"
            fullWidth
            value={formValues.title}
            onChange={(e) => handleChange('title', e.target.value)}
            variant="standard"
            InputProps={{
              style: { 
                fontSize: '1.75rem', 
                fontWeight: 'bold' 
              },
            }}
            required
            error={!!validationErrors.title}
            helperText={validationErrors.title}
          />
        </Box>
        
        <Divider sx={{ my: 1 }} />
        
        {/* Main content grid */}
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            {/* First row: Image (read-only) and Event details */}
            <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ 
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2,
                overflow: 'hidden',
                mb: { xs: 0.5, md: 0.5 }
              }}>
                {localPreviewUrl ? (
                  <img 
                    src={localPreviewUrl}
                    alt={gathering.title}
                    style={{ 
                      maxWidth: '100%',
                      maxHeight: '180px',
                      objectFit: 'contain',
                      borderRadius: 8
                    }} 
                  />
                ) : pendingImageObjectKey === '' ? (
                  <Typography variant="body2" color="text.secondary">Default image will be used</Typography>
                ) : gathering.image_url ? (
                  <img 
                    src={gathering.image_url} 
                    alt={gathering.title} 
                    style={{ 
                      maxWidth: '100%',
                      maxHeight: '180px',
                      objectFit: 'contain',
                      borderRadius: 8
                    }} 
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">No image set</Typography>
                )}
              </Box>
              {/* Minimal controls appended below image to avoid changing existing behavior */}
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', mb: { xs: 2, md: 0 } }}>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleImageFileChange}
                />
                <Button size="small" variant="outlined" onClick={openImagePicker} disabled={isSaving || isUploadingImage}>
                  Change Image
                </Button>
                <Button size="small" variant="text" color="inherit" onClick={handleResetToDefaultImage} disabled={isSaving || isUploadingImage}>
                  Use default image
                </Button>
              </Stack>
              {isUploadingImage && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                  Uploading image...
                </Typography>
              )}
            </Grid>
            
            {/* Event details */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CategoryIcon color="primary" />
                  <Typography variant="h6" fontWeight="medium">
                    Event Details
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Community, Event Size, Category - All in one row */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'row', 
                    justifyContent: 'flex-start', 
                    flexWrap: 'wrap',
                    gap: { xs: 3, md: 4 },
                    mb: 2
                  }}>
                    {/* Community */}
                    <Box sx={{ minWidth: '180px', maxWidth: '250px' }}>
                      <Typography variant="subtitle2" color="text.secondary">Community</Typography>
                      <TextField
                        fullWidth
                        value={formValues.community}
                        onChange={(e) => handleChange('community', e.target.value)}
                        variant="outlined"
                        size="small"
                        required
                        error={!!validationErrors.community}
                        helperText={validationErrors.community}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <GroupsIcon fontSize="small" />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Box>
                    
                    {/* Event Size */}
                    <Box sx={{ minWidth: '180px', maxWidth: '250px' }}>
                      <Typography variant="subtitle2" color="text.secondary">Event Size</Typography>
                      <TextField
                        type="number"
                        fullWidth
                        value={formValues.event_size}
                        onChange={(e) => handleChange('event_size', parseInt(e.target.value, 10) || 0)}
                        variant="outlined"
                        size="small"
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <GroupIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          inputProps: { min: 1 }
                        }}
                      />
                    </Box>
                    
                    {/* Category */}
                    <Box sx={{ minWidth: '180px', maxWidth: '250px' }}>
                      <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                      <TextField
                        select
                        fullWidth
                        value={formValues.category}
                        onChange={(e) => handleChange('category', e.target.value)}
                        variant="outlined"
                        size="small"
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CategoryIcon fontSize="small" />
                            </InputAdornment>
                          )
                        }}
                      >
                        {Object.values(Interest).map((interest) => (
                          <MenuItem key={interest} value={interest}>
                            {interest}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    
                    {/* Owner (read-only) */}
                    {gathering.owner && (
                      <Box sx={{ minWidth: '180px', maxWidth: '250px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Owner</Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          border: '1px solid rgba(0, 0, 0, 0.23)',
                          borderRadius: 1,
                          height: '40px',
                          px: 1.5
                        }}>
                          <InputAdornment position="start" sx={{ mr: 0.5, p: 0 }}>
                            <PersonIcon fontSize="small" color="action" />
                          </InputAdornment>
                          <Avatar 
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              mr: 1, 
                              fontSize: '0.75rem',
                              bgcolor: 'primary.main'
                            }}
                          >
                            {gathering.owner.display_name?.charAt(0) || 'U'}
                          </Avatar>
                          <Typography variant="body1">
                            {gathering.owner.display_name || 'Unknown'}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Gathering Link */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: '140px', flex: '1' }}>
                      <Typography variant="subtitle2" color="text.secondary">Gathering Link</Typography>
                      <TextField
                        fullWidth
                        value={formValues.luma_link}
                        onChange={(e) => handleChange('luma_link', e.target.value)}
                        variant="outlined"
                        size="small"
                        placeholder="https://lu.ma/..."
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LinkIcon fontSize="small" />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Box>
                  </Box>
                  
                  {/* Description and Additional Notes side by side */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: '1 1 45%' }}>
                      <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                      <RichTextEditor
                        content={descriptionToEditorHtml(formValues.description)}
                        onChange={(html) => handleChange('description', html)}
                        placeholder="Describe your gathering..."
                      />
                    </Box>
                    
                    <Box sx={{ flex: '1 1 45%' }}>
                      <Typography variant="subtitle2" color="text.secondary">Additional Notes</Typography>
                      <TextField
                        multiline
                        rows={3}
                        fullWidth
                        value={formValues.additional_notes}
                        onChange={(e) => handleChange('additional_notes', e.target.value)}
                        variant="outlined"
                        size="small"
                        placeholder="Any additional notes..."
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                              <NotesIcon fontSize="small" />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Box>
                  </Box>
                  
                  {/* Flags */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formValues.is_public} 
                          onChange={(e) => handleChange('is_public', e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PublicIcon fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">Public</Typography>
                        </Box>
                      }
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formValues.is_members_only} 
                          onChange={(e) => handleChange('is_members_only', e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PeopleIcon fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">Members Only</Typography>
                        </Box>
                      }
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formValues.is_featured} 
                          onChange={(e) => handleChange('is_featured', e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <StarIcon fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">Featured</Typography>
                        </Box>
                      }
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formValues.is_fabrik_hosted}
                          onChange={(e) => handleChange('is_fabrik_hosted', e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2">Fabrik Hosted</Typography>
                        </Box>
                      }
                    />

                    <Box sx={{ flexBasis: '100%' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formValues.is_builder_gathering}
                            onChange={(e) => handleChange('is_builder_gathering', e.target.checked)}
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <BuildIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography variant="body2">Builder Gathering</Typography>
                          </Box>
                        }
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                        {isPublished
                          ? 'Attendees get the builder follow-up emails instead of the generic sequence. Saved now and sent to HubSpot on the next sync.'
                          : 'Attendees get the builder follow-up emails instead of the generic sequence. Saved now, sent to HubSpot when this gathering is published.'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                    <Button 
                      variant="outlined" 
                      color="inherit" 
                      onClick={handleCancel}
                      startIcon={<CancelIcon />}
                      size="small"
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={handleSave}
                      startIcon={<SaveIcon />}
                      disabled={!isModified || isSaving}
                      size="small"
                    >
                      Save Changes
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default GatheringEditForm;